// ComponentsTable.tsx
import React, { useMemo } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box, Chip, Paper } from '@mui/material';
import CustomAgGrid from '../../../CustomTable/CustomAgGrid';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { currencySymbolMap } from '../../../constants/constants';
import type { WorkOrderIssue } from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import { useTranslation } from 'react-i18next';

const formatAmount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const StatusChip = ({ label }: { label: string }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: '24px',
      fontSize: '14px',
      borderRadius: '8px',
    }}
  />
);

interface ComponentsTableProps {
  components: WorkOrderIssue[] | undefined | null;
}

const ComponentsTable: React.FC<ComponentsTableProps> = ({ components }) => {
  const { t } = useTranslation();
  const { timezone, preferredTimeDisplayMode } = useTimeDisplayMode();

  const rowData = useMemo(() => components ?? [], [components]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'componentId',
        headerName: t('component'),
        minWidth: 160,
        resizable: true,
        flex: 1,
      },
      {
        field: 'subComponentId',
        headerName: t('sub_component_2'),
        minWidth: 160,
        resizable: true,
        flex: 1,
      },
      {
        field: 'plannedStartDate',
        headerName: t('planned_start'),
        minWidth: 140,
        resizable: true,
        valueGetter: (params) =>
          params?.data?.plannedStartDate
            ? convertUtcToDisplayTime(
                params.data.plannedStartDate,
                preferredTimeDisplayMode,
                timezone,
                DATE_FORMAT
              )
            : '-',
      },
      {
        field: 'plannedEndDate',
        headerName: t('planned_end'),
        minWidth: 140,
        resizable: true,
        valueGetter: (params) =>
          params?.data?.plannedEndDate
            ? convertUtcToDisplayTime(
                params.data.plannedEndDate,
                preferredTimeDisplayMode,
                timezone,
                DATE_FORMAT
              )
            : '-',
      },
      {
        field: 'estimatedTime',
        headerName: t('est_labor_hrs'),
        minWidth: 120,
        resizable: true,
      },
      {
        field: 'actualEndDate',
        headerName: t('actual_completion'),
        minWidth: 140,
        resizable: true,
        valueGetter: (params) =>
          params?.data?.actualEndDate
            ? convertUtcToDisplayTime(
                params.data.actualEndDate,
                preferredTimeDisplayMode,
                timezone,
                DATE_FORMAT
              )
            : '-',
      },
      {
        field: 'maintenanceActionPerformed',
        headerName: t('maintenance_action'),
        minWidth: 180,
        resizable: true,
        cellRendererFramework: (params: ICellRendererParams) => (
          <StatusChip label={params.data?.maintenanceActionPerformed ?? '-'} />
        ),
      },
      {
        field: 'actualTime',
        headerName: t('actual_hrs'),
        minWidth: 110,
        resizable: true,
      },
      {
        field: 'partsCost',
        headerName: t('parts_cost'),
        minWidth: 120,
        resizable: true,
        valueGetter: (params) => {
          const code = params?.data?.currencyCode ?? 'DEFAULT';
          const symbol = currencySymbolMap[code] ?? '$';
          const v = Number(params?.data?.partsCost ?? 0) || 0;
          return `${symbol} ${formatAmount(v)}`;
        },
      },
      {
        field: 'laborCost',
        headerName: t('labor_cost'),
        minWidth: 120,
        resizable: true,
        valueGetter: (params) => {
          const code = params?.data?.currencyCode ?? 'DEFAULT';
          const symbol = currencySymbolMap[code] ?? '$';
          const v = Number(params?.data?.laborCost ?? 0) || 0;
          return `${symbol} ${formatAmount(v)}`;
        },
      },
      {
        field: 'totalCost',
        headerName: t('total_cost'),
        minWidth: 120,
        resizable: true,
        valueGetter: (params) => {
          const code = params?.data?.currencyCode ?? 'DEFAULT';
          const symbol = currencySymbolMap[code] ?? '$';
          const v = Number(params?.data?.totalCost ?? 0) || 0;
          return `${symbol} ${formatAmount(v)}`;
        },
      },
      {
        field: 'description',
        headerName: t('description'),
        minWidth: 260,
        resizable: true,
        flex: 1,
      },
    ],
    [preferredTimeDisplayMode, timezone]
  );

  return (
    <Paper
      sx={{
        maxHeight: 420,
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 320 }}>
        <CustomAgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          animateRows
          pagination
          paginationPageSize={10}
          headerHeight={50}
          rowHeight={56}
          domLayout="normal"
        />
      </Box>
    </Paper>
  );
};

export default ComponentsTable;
